/* ============================================================================
   CAPAS BASE DEL MAPA

   Un solo lugar donde viven los fondos de mapa. Antes estaban dentro del mapa
   de planificación, así que el modo vuelo no las tenía: en el iPad el piloto
   veía la ruta sobre un fondo oscuro, sin poder elegir satelital ni relieve —
   que es justo lo que hace falta para reconocer un claro o una helisuperficie.

   Compartirlas también evita que se separen. Si mañana se cambia el servidor
   de una capa, se cambia acá y las dos pantallas quedan iguales.

   IMPORTANTE: cada servidor nuevo hay que agregarlo a `imgSrc` en la política
   de contenido de server/index.js. Sin eso el navegador bloquea los mosaicos
   en silencio y el mapa se ve vacío.
   ========================================================================== */

import L from "leaflet";

/* ============================================================================
   DOS CAMINOS PARA CADA MOSAICO

   Ni el proxy ni la conexión directa funcionan siempre:

   - **Directo** (el navegador le pide al servidor de mapas) funciona en la
     computadora, pero NO en el iPad cuando la página se sirve con un
     certificado propio: el navegador degrada el contexto de seguridad y
     bloquea las peticiones a otros dominios.

   - **Por el proxy** (el navegador le pide a nuestro servidor, y él sale a
     buscarlo) resuelve el iPad, pero depende de que el proceso de Node pueda
     alcanzar los servidores de mapas. Un proxy corporativo o una VPN
     configurados en el sistema los usa el navegador y no los usa Node.

   Poner solo el proxy fue un error: arregló el iPad y **rompió la web**, donde
   antes andaba.

   Ahora cada capa arranca por el proxy y, si no llega ningún mosaico, se pasa
   sola al camino directo. Con que UNO de los dos funcione, hay mapa.
   ========================================================================== */

/* ═══════════════════════════════════════════════════════════════════════════
   MAPTILER — la capa del operador

   El operador tiene cuenta en MapTiler Cloud con el mapa **Topo v4**, que es
   el que sirve en sierra: curvas de nivel, sombreado y sendas.

   ══════════════════════════════════════════════════════════════════════════
   LO QUE SUS CONDICIONES PERMITEN Y LO QUE NO

   PERMITEN: «Results of requests can be stored in a temporary personal cache
   (browser cache, mobile app cache, etc.) for use by a single end-user only.»

   → O sea: **se puede navegar y lo que se ve queda guardado.** Volando por la
     misma zona dos veces, la segunda ya no pide nada a la red.

   NO PERMITEN: «Bulk Downloading. Unless otherwise agreed in writing with
   MapTiler, it is prohibited to batch or excessive bulk download of map
   tiles.»

   → O sea: **el botón de descargar una región NO se puede usar con esta
     clave.** Para eso hace falta MapTiler Data —el producto de mosaicos
     autohospedados— o un permiso por escrito.

   Por eso MapTiler entra como capa navegable y NO como origen de descarga.
   ══════════════════════════════════════════════════════════════════════════ */

/** Dónde guarda el operador su clave de MapTiler. */
export const CLAVE_MAPTILER = "hcClaveMapTiler";

/**
 * La clave de MapTiler que va a usar la aplicación.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * DOS ORÍGENES, Y EL ORDEN IMPORTA
 *
 * 1. **La clave del operador**, puesta al compilar en `VITE_MAPTILER_KEY`.
 *    Viaja dentro de la aplicación: ningún piloto tiene que pegar nada, y en
 *    un dispositivo nuevo el mapa anda desde el primer arranque.
 *
 * 2. **Una clave propia**, que un piloto puede pegar en el panel.
 *
 * Manda la propia cuando existe. El motivo es de facturación: si un piloto
 * tiene su cuenta, sus mosaicos los paga él y no salen de la cuota del
 * operador. Y si la clave del operador se agota a fin de mes, el que tenga la
 * suya sigue volando con mapa.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LA CLAVE SIEMPRE ES VISIBLE, Y ESO NO SE PUEDE EVITAR
 *
 * En una aplicación que corre en el navegador, cualquier clave que se use está
 * en el dispositivo del usuario: la ve quien mire el tráfico de red. No hay
 * forma de esconderla, ni con esta aplicación ni con ninguna otra.
 *
 * La protección que corresponde NO es esconderla: es **restringirla por
 * dominio** en el panel de MapTiler, para que solo funcione desde las
 * direcciones del operador. Eso es lo que hace que una clave robada no sirva.
 * ══════════════════════════════════════════════════════════════════════════
 */
export function claveMapTiler() {
  try {
    const propia = (localStorage.getItem(CLAVE_MAPTILER) || "").trim();
    if (propia) return propia;
  } catch { /* modo privado: se usa la del operador */ }

  /* La del operador, puesta al compilar. `import.meta.env` lo reemplaza Vite
     por el valor literal, así que no hace falta ninguna petición. */
  try {
    return (import.meta.env?.VITE_MAPTILER_KEY || "").trim();
  } catch { return ""; }
}

/** ¿De dónde salió la clave que se está usando? */
export function origenDeClave() {
  try {
    if ((localStorage.getItem(CLAVE_MAPTILER) || "").trim()) return "propia";
  } catch { /* sigue */ }
  try {
    if ((import.meta.env?.VITE_MAPTILER_KEY || "").trim()) return "operador";
  } catch { /* sigue */ }
  return null;
}

/**
 * Saca la clave de lo que el operador haya pegado.
 *
 * ═══ POR QUÉ ESTO HACE FALTA ═══
 *
 * El panel de MapTiler muestra la dirección COMPLETA de los mosaicos, no la
 * clave suelta. Lo natural es copiar esa dirección entera y pegarla — y eso es
 * lo que pasó.
 *
 * Guardándola tal cual, la aplicación armaba
 * `...?key=https://api.maptiler.com/...` y MapTiler devolvía 403: el mapa no
 * aparecía y nada explicaba por qué.
 *
 * Rechazar lo pegado con un «esto no es una clave» sería técnicamente correcto
 * e inútil: la clave ESTÁ ahí adentro. Se extrae.
 */
export function extraerClave(pegado) {
  const t = String(pegado || "").trim();
  if (!t) return "";

  /* Una dirección completa: la clave va en `?key=`. */
  const enUrl = /[?&]key=([^&\s]+)/.exec(t);
  if (enUrl) return enUrl[1];

  /* Si pegó una dirección SIN `key=`, no hay clave que sacar: devolver el
     texto entero produciría el mismo 403 silencioso. */
  if (/^https?:\/\//i.test(t)) return "";

  return t;
}

export function guardarClaveMapTiler(clave) {
  try {
    localStorage.setItem(CLAVE_MAPTILER, extraerClave(clave));
  } catch { /* modo privado */ }
}

/**
 * Direcciones de MapTiler para el mapa que el operador tenga.
 *
 * `256` es el tamaño de mosaico: sin él, MapTiler devuelve mosaicos de 512 px
 * y Leaflet los dibuja al doble de tamaño, con el mapa borroso y los rótulos
 * ilegibles.
 */
export function capaMapTiler(mapa = "topo-v2") {
  const clave = claveMapTiler();
  if (!clave) return null;

  /* El formato sale del mapa, no fijo: pedir un satelital en PNG devuelve un
     archivo que pesa el triple y no se ve mejor, y pedir un vectorial en JPEG
     lo deja con los bordes sucios. */
  const def = MAPAS_MAPTILER.find((m) => m.clave === mapa);
  const formato = def?.formato || "png";

  return `https://api.maptiler.com/maps/${mapa}/256/{z}/{x}/{y}.${formato}?key=${clave}`;
}

/** Los mapas de MapTiler que sirven para volar. */
export const MAPAS_MAPTILER = [
  {
    clave: "streets-v2",
    nombre: "MapTiler Calles",
    nota: "Mapa base. Más legible que el de OpenStreetMap: menos ruido y "
      + "mejores rótulos.",
    /* JPEG en las capas con imagen y PNG en las vectoriales: un satelital en
       PNG pesa el triple y no se ve mejor. */
    formato: "png",
  },
  {
    clave: "topo-v2",
    nombre: "MapTiler Topográfico",
    nota: "Curvas de nivel y sombreado. El mejor para sierra.",
    formato: "png",
  },
  {
    clave: "outdoor-v2",
    nombre: "MapTiler Outdoor",
    nota: "Sendas, relieve y cobertura del terreno.",
    formato: "png",
  },
  {
    clave: "hybrid",
    nombre: "MapTiler Satelital híbrido",
    nota: "Imagen satelital CON rótulos y caminos encima. Es la que sirve para "
      + "reconocer una zona de aterrizaje y saber dónde queda.",
    formato: "jpg",
  },
  {
    clave: "satellite",
    nombre: "MapTiler Satelital",
    nota: "Imagen satelital sola, sin rótulos.",
    formato: "jpg",
  },
];

/** Las direcciones directas, para el respaldo. Iguales a las del servidor. */
export const DIRECTAS = {
  calles: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  relieve: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
  sombreado: "https://server.arcgisonline.com/ArcGIS/rest/services/Elevation/World_Hillshade/MapServer/tile/{z}/{y}/{x}",
  curvas: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
  satelital: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
};

/**
 * Atribución obligatoria de MapTiler.
 *
 * No es cortesía: sus condiciones la exigen, y sin ella el uso no está
 * licenciado. Va en el mapa, visible.
 */
export const ATRIBUCION_MAPTILER = '<a href="https://www.maptiler.com/copyright/">© MapTiler</a> '
  + '<a href="https://www.openstreetmap.org/copyright">© OpenStreetMap</a>';

/**
 * Mantiene la capa en el camino que esté trayendo mosaicos.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * HAY DOS CAMINOS Y NINGUNO ANDA SIEMPRE
 *
 * · **Por el proxy** (`/mapas/...`): el navegador le pide a nuestro servidor y
 *   él sale a buscar. Es el ÚNICO que funciona en el iPad cuando la página se
 *   sirve con certificado propio — iOS degrada el contexto de seguridad y
 *   bloquea las peticiones a otros dominios.
 * · **Directo**: funciona en la computadora, y falla si el servidor de la
 *   aplicación no tiene salida a internet.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * EL ERROR QUE DEJABA EL IPAD EN NEGRO
 *
 * Antes esto cambiaba UNA sola vez y para siempre. Si el proxy fallaba al
 * arrancar —el servidor todavía levantando, un timeout, un mosaico cancelado
 * al mover el mapa— la capa se pasaba al directo… **y en el iPad el directo
 * está bloqueado.** El mapa quedaba negro el resto de la sesión, sin forma de
 * volver, mientras en la computadora el mismo cambio no se notaba porque ahí
 * el directo sí anda.
 *
 * Esa es toda la diferencia entre «en la Mac se ve y en el iPad no».
 *
 * Ahora ALTERNA: si el camino actual junta errores seguidos, se prueba el
 * otro. Si el otro tampoco trae, se vuelve. **Con que uno de los dos funcione,
 * hay mapa** — que es lo que este módulo siempre dijo que hacía.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * DOS DETALLES QUE NO SON ADORNO
 *
 * · **Lo que cuenta es la RACHA, no el total.** Antes bastaba UN acierto
 *   histórico para que el respaldo no se activara nunca más: con unos pocos
 *   mosaicos viejos en la caché del servidor, la capa quedaba atrapada en un
 *   camino que ya no traía nada y el piloto veía el mapa a medio dibujar.
 * · **Hay un tope de cambios.** Sin él, con los dos caminos caídos la capa
 *   alternaría para siempre, pidiendo mosaicos sin parar y gastando batería
 *   en vuelo. Al llegar al tope se queda donde está y `vigilarMosaicos` avisa.
 *
 * @returns {Function} para dejar de vigilar
 */
export function conRespaldoDirecto(capa, clave, opciones = {}) {
  const { erroresParaCambiar = 4, alCambiar, maximoCambios = 4 } = opciones;

  const directa = DIRECTAS[clave];
  if (!capa || typeof capa.on !== "function" || !directa) return () => {};

  /* La dirección con la que nació la capa: el camino al que se vuelve. */
  const original = capa._url || (typeof capa.getUrl === "function" ? capa.getUrl() : null);
  if (!original) return () => {};

  let errores = 0;
  let cambios = 0;
  let enDirecta = false;

  /* Un acierto corta la racha: lo que decide es si AHORA trae, no si trajo
     algo alguna vez. */
  const alCargar = () => { errores = 0; };

  const alFallar = () => {
    errores += 1;
    if (errores < erroresParaCambiar || cambios >= maximoCambios) return;

    errores = 0;
    cambios += 1;
    enDirecta = !enDirecta;

    /* `setUrl` reemplaza la plantilla y vuelve a pedir lo que hace falta, sin
       dar de baja la capa: el mapa no parpadea. */
    capa.setUrl(enDirecta ? directa : original);
    capa.options.subdomains = enDirecta ? ["a", "b", "c"] : [];
    alCambiar?.(clave, enDirecta ? "directo" : "proxy");
  };

  capa.on("tileload", alCargar);
  capa.on("tileerror", alFallar);
  return () => {
    capa.off("tileload", alCargar);
    capa.off("tileerror", alFallar);
  };
}


const ATRIB_OSM = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

export function construirCapas(tactil) {
  /**
   * Ajustes de rendimiento comunes a todas las capas.
   *
   * `maxNativeZoom` es el que más se nota: sin él, al pasar del zoom máximo
   * del servidor Leaflet pide mosaicos que no existen, espera, recibe 404 y
   * deja huecos grises. Con él, amplía el último nivel disponible y responde
   * al instante.
   *
   * `updateWhenIdle` en escritorio se deja en falso para que el mapa se
   * refresque mientras se arrastra, no al soltar.
   */
  const comunes = {
    updateWhenIdle: tactil,
    updateWhenZooming: false,
    keepBuffer: 4,
    crossOrigin: true,
  };

  /* Con nombre, para poder agregarle las capas del operador abajo.

     Estaba como `return { ... };` directo, y mi bloque quedó DESPUÉS del
     return: código inalcanzable que el lint agarró de inmediato. */
  const capas = {
    calles: L.tileLayer("/mapas/calles/{z}/{x}/{y}.png", {
      ...comunes,
      maxZoom: 19,
      attribution: ATRIB_OSM,
    }),
    /**
     * Relieve rápido: topográfico de Esri, sobre CDN.
     *
     * OpenTopoMap está servido por voluntarios, con límite de peticiones y
     * renderizado lento: al arrastrar el mapa los mosaicos tardaban segundos
     * en aparecer. Se conserva abajo como "Curvas de nivel" para quien
     * necesite ese detalle y pueda esperar, pero deja de ser el relieve por
     * defecto.
     */
    relieve: L.tileLayer(
      "/mapas/relieve/{z}/{x}/{y}.png",
      {
        ...comunes,
        maxZoom: 19,
        maxNativeZoom: 19,
        attribution: "Esri, HERE, Garmin, Intermap, USGS, NGA y la comunidad de usuarios SIG",
      },
    ),
    /** Sombreado del terreno, sin etiquetas. Muy rápido y muy legible en montaña. */
    sombreado: L.tileLayer(
      "/mapas/sombreado/{z}/{x}/{y}.png",
      {
        ...comunes,
        maxZoom: 19,
        maxNativeZoom: 16,
        attribution: "Sombreado: Esri, Airbus DS, USGS, NGA, NASA, CGIAR, NCEAS y otros",
      },
    ),
    curvas: L.tileLayer("/mapas/curvas/{z}/{x}/{y}.png", {
      ...comunes,
      maxZoom: 17,
      maxNativeZoom: 15,
      attribution: `${ATRIB_OSM} · SRTM | <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)`,
    }),
    // Imagen satelital de Esri: gratis, sin clave, y con la resolución
    // suficiente para reconocer una helisuperficie o un claro en el terreno.
    // Exige mantener la atribución visible.
    satelital: L.tileLayer(
      "/mapas/satelital/{z}/{x}/{y}.png",
      {
        ...comunes,
        /* ═══ MÁS ACERCAMIENTO QUE EL RESTO, Y ES DE SEGURIDAD ═══

           El operador lo pidió para reconocer zonas de posible aterrizaje.
           Tiene razón: a zoom 19 no se distingue si un claro tiene piedras,
           cables o un alambrado, y esa es una decisión de seguridad que se
           toma mirando.

           Esri publica hasta zoom 23 en zonas urbanas y bastante menos en
           campo abierto. `maxNativeZoom: 19` es hasta dónde se PIDEN mosaicos;
           `maxZoom: 22` es hasta dónde se puede acercar. Por encima del nativo,
           Leaflet agranda el último nivel que existe: se ve borroso, pero se
           ve — y en una decisión de aterrizaje, borroso es mejor que nada.

           Pedir mosaicos por encima del nativo devolvería 404 y dejaría el
           mapa en blanco, que es lo contrario de lo que hace falta. */
        maxZoom: 22,
        maxNativeZoom: 19,
        attribution:
          "Imagen: Esri, Maxar, Earthstar Geographics y la comunidad de usuarios SIG",
      },
    ),
  };

  /* ═══ LAS CAPAS DEL OPERADOR ═══

     Se agregan acá, no en otro lado: `capasDelOperador()` estaba escrita y
     **el mapa la importaba sin usarla**. El panel decía «hay 5 capas de
     MapTiler» y el selector no mostraba ninguna — la decimoquinta vez que un
     módulo queda huérfano en este proyecto.

     Solo si hay clave: sin ella MapTiler devuelve 403 y el mapa se ve negro,
     y el piloto elegiría una capa que no puede funcionar. */
  for (const [clave, nombre] of capasDelOperador()) {
    const direccion = direccionDeCapa(clave);
    if (!direccion) continue;

    capas[clave] = L.tileLayer(direccion, {
      ...comunes,
      /* El satelital de MapTiler llega más cerca que el resto: es la capa con
         la que se reconoce una zona de aterrizaje. */
      maxZoom: /satellite|hybrid/.test(clave) ? 22 : 20,
      maxNativeZoom: /satellite|hybrid/.test(clave) ? 20 : 19,
      /* Obligatoria: el plan gratuito exige la atribución y sin ella el uso no
         está licenciado. */
      attribution: ATRIBUCION_MAPTILER,
      alt: nombre,
    });
  }

  return capas;
}

/** Las capas que se le ofrecen al piloto, en el orden en que se muestran. */
export const CAPAS_OFRECIDAS = [
  ["calles", "Calles"],
  ["relieve", "Relieve"],
  ["sombreado", "Sombreado"],
  ["curvas", "Curvas de nivel"],
  ["satelital", "Satélite"],
];

/**
 * Las capas del operador, que solo aparecen si cargó su clave.
 *
 * Ofrecer una capa que no puede funcionar —sin clave, MapTiler devuelve 403 y
 * el mapa se ve negro— es peor que no ofrecerla: el piloto la elige, no ve
 * nada, y desconfía del mapa entero.
 */
export function capasDelOperador() {
  if (!claveMapTiler()) return [];
  return MAPAS_MAPTILER.map((m) => [`maptiler:${m.clave}`, m.nombre]);
}

/** ¿Esta clave de capa es de MapTiler? */
export function esMapTiler(clave) {
  return typeof clave === "string" && clave.startsWith("maptiler:");
}

/** La dirección de una capa, sea propia o del operador. */
export function direccionDeCapa(clave) {
  if (esMapTiler(clave)) return capaMapTiler(clave.slice("maptiler:".length));
  return DIRECTAS[clave] || null;
}

/** La que se usa mientras el piloto no elija otra. */
export const CAPA_POR_DEFECTO = "calles";

/**
 * Vigila que los mosaicos lleguen de verdad.
 *
 * Un mapa sin mosaicos se ve NEGRO y no da ningún error. Pasó durante días:
 * la ruta se dibujaba encima del vacío y parecía un error del código de la
 * aplicación, cuando el problema estaba en la red.
 *
 * Esto escucha a la capa: si en unos segundos no cargó ni un mosaico, avisa
 * con el motivo. Si después llegan, se retira el aviso.
 *
 * @param {object} capa   la capa de Leaflet a vigilar
 * @param {Function} alCambiar recibe `null` cuando está todo bien, o un texto
 * @param {number} [esperaMs]
 * @returns {Function} para dejar de vigilar
 */
/**
 * Pregunta al servidor por qué no llegan los mosaicos.
 *
 * El cartel decía «el servidor no pudo traer los mapas» y ahí terminaba. Eso
 * no alcanza: el motivo puede ser que el servidor no tenga internet, que se
 * esté entrando por un servidor viejo sin el proxy, o que el servidor de mapas
 * esté rechazando los pedidos. Cada uno se arregla distinto, y sin saber cuál
 * es, hay que probar los tres.
 *
 * Se pide un mosaico concreto —el del mundo entero, que siempre existe— y se
 * traduce la respuesta a algo accionable.
 */
export async function diagnosticarMosaicos(capa = "calles", traer = fetch) {
  const url = `/mapas/${capa}/1/0/0.png`;
  let r;
  try {
    r = await traer(url, { cache: "no-store" });
  } catch {
    return "No se pudo contactar al servidor de la aplicación. ¿Sigue corriendo?";
  }

  if (r.status === 404) {
    /* El proxy contesta 404 solo si la capa o las coordenadas son inválidas.
       Un servidor sin el proxy montado devuelve el index.html o un 404 propio:
       en los dos casos, se está entrando por el servidor equivocado. */
    return "Este servidor no tiene el proxy de mapas. Suele pasar entrando por "
      + "una dirección vieja o sin el puerto: usa la que muestra el código QR.";
  }
  if (r.status === 504) return "El servidor no tiene internet para descargar los mapas.";
  if (r.status === 502) return "El servidor de mapas rechazó el pedido. Puede ser un límite de uso.";
  if (!r.ok) return `El servidor contestó ${r.status} al pedir un mosaico.`;

  /* Contestó bien: si igual no se ven, lo que devuelve no es una imagen. Pasa
     cuando el que responde es el index.html en lugar del proxy. */
  const tipo = r.headers?.get?.("content-type") || "";
  if (!tipo.includes("image")) {
    return `El servidor contestó, pero con «${tipo || "tipo desconocido"}» en lugar de una imagen. `
      + "Casi seguro estás entrando por un servidor que no es el de HeliCalc.";
  }

  return "Los mosaicos llegan al pedirlos de a uno. Puede ser una caída pasajera: prueba recargar.";
}

/**
 * Vigila que los mosaicos lleguen de verdad.
 *
 * Un mapa sin mosaicos se ve NEGRO y no da ningún error. Pasó durante días: la
 * ruta se dibujaba encima del vacío y parecía un error del código de la
 * aplicación, cuando el problema estaba en la red.
 *
 * Si en unos segundos no llegó ninguno, pregunta al servidor por qué y muestra
 * el motivo concreto. Si después llegan, se retira el aviso.
 *
 * @param {object} capa   la capa de Leaflet a vigilar
 * @param {Function} alCambiar recibe `null` cuando está todo bien, o un texto
 * @param {object} [opciones]
 * @returns {Function} para dejar de vigilar
 */
export function vigilarMosaicos(capa, alCambiar, opciones = {}) {
  const { esperaMs = 8000, clave = "calles", traer = undefined } = opciones;
  if (!capa || typeof capa.on !== "function") return () => {};

  let cargados = 0;
  let vivo = true;

  const alCargar = () => {
    cargados += 1;
    if (vivo && cargados === 1) alCambiar(null);
  };
  const alFallar = () => {};

  capa.on("tileload", alCargar);
  capa.on("tileerror", alFallar);

  const reloj = setTimeout(async () => {
    if (!vivo || cargados > 0) return;
    const motivo = await diagnosticarMosaicos(clave, traer || fetch);
    if (vivo && cargados === 0) alCambiar(motivo);
  }, esperaMs);

  return () => {
    vivo = false;
    clearTimeout(reloj);
    capa.off("tileload", alCargar);
    capa.off("tileerror", alFallar);
  };
}

